// OpsRequestCard — one ticket, with whatever actions the viewer may take
// (Sep 25, 2026). Used by the Requests page and the deal-page card.
//
// Actions render by role but the DATABASE decides: every button is a plain
// table write and the ops_requests_guard trigger rejects anything the caller
// may not do. Errors from that guard are shown inline.
//
//   assignee / leadership : Start · Waiting (on whom + note) · Done (note) · Resume
//   requester             : Withdraw (reason) while not Done · Edit while New
//   leadership            : Reassign
//   anyone who can see it : Reply (comment thread)

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import DealLink from "../DealLink";
import {
  CATEGORIES, CATEGORY_ICON, CATEGORY_LABEL, STATUS_CLASS, STATUS_LABEL, WAITING_ON_LABEL, WAITING_ON_OPTIONS,
  addComment, agingLabel, agingTone, canEditFields, canReassign, canWithdraw, canWork, editRequest,
  fetchAttachments, fetchEvents, fmtNeededBy, fmtWhen, isOpen, isPastNeededBy, isRequester,
  markDone, markWaiting, reassignRequest, reopenRequest, resumeRequest, signedUrl, startRequest, withdrawRequest,
  type ActorContext, type OpsAttachment, type OpsCategory, type OpsPriority, type OpsRequest, type OpsRequestEvent,
  type OpsWaitingOn, type RosterLite,
} from "../../lib/opsRequests";

type Panel = null | "waiting" | "done" | "withdraw" | "reassign" | "edit";

interface Props {
  r: OpsRequest;
  actor: ActorContext;
  roster: Record<string, RosterLite>;
  onChanged: () => void;
  /** Start expanded (thread + attachments visible). */
  defaultExpanded?: boolean;
  /** Same job has an open Dispatch Question. */
  hasOpenQuestion?: boolean;
  /** Deal-page variant: no job link (you're already on it), tighter header. */
  onDealPage?: boolean;
}

function Chip({ className, children, title }: { className: string; children: ReactNode; title?: string }) {
  return <span title={title} className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${className}`}>{children}</span>;
}

export default function OpsRequestCard({ r, actor, roster, onChanged, defaultExpanded = false, hasOpenQuestion = false, onDealPage = false }: Props) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [panel, setPanel] = useState<Panel>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [events, setEvents] = useState<OpsRequestEvent[] | null>(null);
  const [attachments, setAttachments] = useState<OpsAttachment[] | null>(null);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [reply, setReply] = useState("");
  // Panel fields
  const [waitingOn, setWaitingOn] = useState<OpsWaitingOn>("homeowner");
  const [note, setNote] = useState("");
  const [reassignTo, setReassignTo] = useState("");
  const [editCat, setEditCat] = useState<OpsCategory>(r.category);
  const [editPri, setEditPri] = useState<OpsPriority>(r.priority);
  const [editBody, setEditBody] = useState(r.body);
  const [editNeeded, setEditNeeded] = useState(r.needed_by ?? "");

  const name = useCallback((id: string | null | undefined) => (id ? roster[id]?.full_name ?? "—" : "—"), [roster]);
  const tone = agingTone(r);
  const open = isOpen(r);
  const work = canWork(r, actor);
  const mine = isRequester(r, actor);

  const loadThread = useCallback(async () => {
    try {
      const [ev, att] = await Promise.all([fetchEvents(r.id), fetchAttachments(r.id)]);
      setEvents(ev); setAttachments(att);
      // Signed URLs for image thumbnails only; documents sign on click.
      const imgs = att.filter((a) => (a.mime_type ?? "").startsWith("image/"));
      const pairs = await Promise.all(imgs.map(async (a) => [a.id, await signedUrl(a.storage_path)] as const));
      setUrls(Object.fromEntries(pairs.filter(([, u]) => !!u) as Array<[string, string]>));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't load the thread.");
    }
  }, [r.id]);

  useEffect(() => { if (expanded) void loadThread(); }, [expanded, loadThread, r.updated_at]);

  const run = async (fn: () => Promise<unknown>, after?: () => void) => {
    setBusy(true); setErr(null);
    try { await fn(); setPanel(null); setNote(""); after?.(); onChanged(); if (expanded) void loadThread(); }
    catch (e) { setErr(e instanceof Error ? e.message : "Something went wrong."); }
    finally { setBusy(false); }
  };

  const openDoc = async (a: OpsAttachment) => {
    const w = window.open("about:blank", "_blank");
    const u = await signedUrl(a.storage_path);
    if (u && w) w.location.href = u; else if (!u) { w?.close(); setErr("Couldn't open that file."); }
  };

  const leadershipRoster = useMemo(
    () => Object.values(roster).filter((m) => m.is_active !== false && m.id !== r.assigned_to)
      .sort((a, b) => a.full_name.localeCompare(b.full_name)),
    [roster, r.assigned_to],
  );

  const borderClass = r.priority === "urgent" && open
    ? "border-rose-300 border-l-4 border-l-rose-500"
    : tone === "red" ? "border-rose-200"
    : tone === "amber" ? "border-amber-200"
    : "border-slate-200";

  return (
    <div className={`rounded-xl border bg-white p-3 sm:p-4 shadow-sm ${borderClass} ${!open ? "opacity-80" : ""}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {onDealPage
              ? <span className="font-bold text-bardin-ink">{CATEGORY_ICON[r.category]} {CATEGORY_LABEL[r.category]}</span>
              : <DealLink id={r.deal_id} className="font-bold text-bardin-ink hover:text-bardin-teal">{r.homeowner_name || "(no name)"}</DealLink>}
            <Chip className={STATUS_CLASS[r.status]}>{STATUS_LABEL[r.status]}</Chip>
            {r.priority === "urgent" && open && <Chip className="bg-rose-100 text-rose-700">URGENT</Chip>}
            {open && (
              <Chip className={tone === "red" ? "bg-rose-100 text-rose-700" : tone === "amber" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}
                    title={`Filed ${fmtWhen(r.created_at)}`}>
                {agingLabel(r)}
              </Chip>
            )}
            {r.needed_by && open && (
              <Chip className={isPastNeededBy(r) ? "bg-rose-100 text-rose-700" : "bg-sky-50 text-sky-800"}>
                needed {fmtNeededBy(r.needed_by)}
              </Chip>
            )}
            {hasOpenQuestion && (
              <button type="button" onClick={() => navigate(`/deal/${r.deal_id}?tab=comms`)}
                      className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-teal-50 text-bardin-teal border border-teal-200"
                      title="An open Dispatch Question exists on this job">
                📡 open question on this job
              </button>
            )}
          </div>
          {!onDealPage && (
            <div className="text-sm text-slate-600 mt-0.5 truncate">
              {r.property_address || "—"}{r.deal_stage ? <span className="text-slate-400"> · {r.deal_stage.replace(/_/g, " ")}</span> : null}
            </div>
          )}
          <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-2">
            {!onDealPage && <span>{CATEGORY_ICON[r.category]} {CATEGORY_LABEL[r.category]}</span>}
            <span>from <span className="font-medium text-slate-700">{name(r.requested_by)}</span></span>
            <span>· to <span className="font-medium text-slate-700">{name(r.assigned_to)}</span></span>
            <span>· {fmtWhen(r.created_at)}</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <p className="mt-2 text-sm text-bardin-ink whitespace-pre-wrap">{r.body}</p>

      {/* State notes */}
      {r.status === "waiting" && (
        <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-1.5 text-xs text-amber-900">
          ⏸ Waiting on {WAITING_ON_LABEL[r.waiting_on ?? "other"]}{r.waiting_note ? ` — ${r.waiting_note}` : ""}
          {r.waiting_at ? <span className="text-amber-700"> · since {fmtWhen(r.waiting_at)}</span> : null}
        </div>
      )}
      {r.status === "done" && (
        <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs text-emerald-900">
          ✓ Done by {name(r.done_by)} · {fmtWhen(r.done_at)}{r.done_note ? ` — ${r.done_note}` : ""}
        </div>
      )}
      {r.status === "withdrawn" && (
        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-1.5 text-xs text-slate-600">
          ↩ Withdrawn · {fmtWhen(r.withdrawn_at)}{r.withdraw_reason ? ` — ${r.withdraw_reason}` : ""}
        </div>
      )}

      {/* Actions */}
      {panel === null && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {work && r.status === "new" && (
            <button type="button" disabled={busy} onClick={() => run(() => startRequest(r.id))}
                    className="px-3 py-1.5 rounded-lg bg-bardin-ink text-white text-xs font-semibold">▶ Start</button>
          )}
          {work && r.status === "waiting" && (
            <button type="button" disabled={busy} onClick={() => run(() => resumeRequest(r.id))}
                    className="px-3 py-1.5 rounded-lg bg-bardin-ink text-white text-xs font-semibold">▶ Resume</button>
          )}
          {work && open && r.status !== "waiting" && (
            <button type="button" disabled={busy} onClick={() => { setNote(""); setPanel("waiting"); }}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold">⏸ Waiting</button>
          )}
          {work && open && (
            <button type="button" disabled={busy} onClick={() => { setNote(""); setPanel("done"); }}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold">✓ Done</button>
          )}
          {work && !open && canReassign(actor) && (
            <button type="button" disabled={busy} onClick={() => run(() => reopenRequest(r.id))}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold">↺ Reopen</button>
          )}
          {canWithdraw(r, actor) && (mine || !work) && (
            <button type="button" disabled={busy} onClick={() => { setNote(""); setPanel("withdraw"); }}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-xs font-semibold">↩ Withdraw</button>
          )}
          {canEditFields(r, actor) && (
            <button type="button" disabled={busy}
                    onClick={() => { setEditCat(r.category); setEditPri(r.priority); setEditBody(r.body); setEditNeeded(r.needed_by ?? ""); setPanel("edit"); }}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-xs font-semibold">✎ Edit</button>
          )}
          {canReassign(actor) && open && (
            <button type="button" disabled={busy} onClick={() => { setReassignTo(""); setPanel("reassign"); }}
                    className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-xs font-semibold">🔁 Reassign</button>
          )}
          <button type="button" onClick={() => setExpanded((v) => !v)}
                  className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 text-xs font-semibold ml-auto">
            {expanded ? "Hide thread" : "💬 Thread"}
          </button>
        </div>
      )}

      {panel === "waiting" && (
        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
          <div className="text-xs font-semibold text-amber-900">Waiting on…</div>
          <div className="flex flex-wrap gap-1.5">
            {WAITING_ON_OPTIONS.map((w) => (
              <button key={w} type="button" onClick={() => setWaitingOn(w)} aria-pressed={waitingOn === w}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${waitingOn === w ? "bg-amber-600 text-white border-amber-600" : "bg-white text-slate-700 border-slate-300"}`}>
                {WAITING_ON_LABEL[w]}
              </button>
            ))}
          </div>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What are you waiting for? (optional)"
                 className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setPanel(null)} className="px-3 py-1.5 text-xs text-slate-600">Cancel</button>
            <button type="button" disabled={busy} onClick={() => run(() => markWaiting(r.id, waitingOn, note))}
                    className="px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold">Mark waiting</button>
          </div>
        </div>
      )}

      {panel === "done" && (
        <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2">
          <div className="text-xs font-semibold text-emerald-900">Done — what happened? {name(r.requested_by)} gets this by text.</div>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Met the adjuster, approved on the spot." autoFocus
                 className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setPanel(null)} className="px-3 py-1.5 text-xs text-slate-600">Cancel</button>
            <button type="button" disabled={busy} onClick={() => run(() => markDone(r.id, note))}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold">✓ Mark done</button>
          </div>
        </div>
      )}

      {panel === "withdraw" && (
        <div className="mt-3 rounded-lg border border-slate-300 bg-slate-50 p-3 space-y-2">
          <div className="text-xs font-semibold text-slate-800">Withdraw this request — why?</div>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Homeowner cancelled · handled it myself" autoFocus
                 className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
          <p className="text-[11px] text-slate-500">
            It won't count as done. {r.status === "in_progress" ? `${name(r.assigned_to)} already started it, so they'll get a text to stop.` : `${name(r.assigned_to)} gets a note on their board.`}
          </p>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setPanel(null)} className="px-3 py-1.5 text-xs text-slate-600">Cancel</button>
            <button type="button" disabled={busy || note.trim().length === 0} onClick={() => run(() => withdrawRequest(r.id, note))}
                    className="px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-semibold disabled:opacity-50">↩ Withdraw</button>
          </div>
        </div>
      )}

      {panel === "reassign" && (
        <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50 p-3 space-y-2">
          <div className="text-xs font-semibold text-violet-900">Reassign to</div>
          <select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm bg-white">
            <option value="">Pick a person…</option>
            {leadershipRoster.map((m) => (
              <option key={m.id} value={m.id}>{m.full_name} · {m.role.replace(/_/g, " ")}</option>
            ))}
          </select>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setPanel(null)} className="px-3 py-1.5 text-xs text-slate-600">Cancel</button>
            <button type="button" disabled={busy || !reassignTo} onClick={() => run(() => reassignRequest(r.id, reassignTo))}
                    className="px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold disabled:opacity-50">Reassign</button>
          </div>
        </div>
      )}

      {panel === "edit" && (
        <div className="mt-3 rounded-lg border border-slate-300 bg-slate-50 p-3 space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {CATEGORIES.map((c) => (
              <button key={c.key} type="button" onClick={() => setEditCat(c.key)} aria-pressed={editCat === c.key}
                      className={`px-2 py-1 rounded-full text-[11px] font-semibold border ${editCat === c.key ? "bg-bardin-ink text-white border-bardin-ink" : "bg-white text-slate-700 border-slate-300"}`}>
                {c.icon} {c.label}
              </button>
            ))}
          </div>
          <textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={3}
                    className="w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
          <div className="flex flex-wrap gap-3 items-center">
            <label className="text-xs text-slate-600">Needed by{" "}
              <input type="date" value={editNeeded} onChange={(e) => setEditNeeded(e.target.value)}
                     className="rounded border border-slate-300 px-2 py-1 text-xs" />
            </label>
            <label className="text-xs text-slate-600 inline-flex items-center gap-1">
              <input type="checkbox" checked={editPri === "urgent"} onChange={(e) => setEditPri(e.target.checked ? "urgent" : "normal")} /> Urgent
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setPanel(null)} className="px-3 py-1.5 text-xs text-slate-600">Cancel</button>
            <button type="button" disabled={busy || editBody.trim().length === 0}
                    onClick={() => run(() => editRequest(r.id, { category: editCat, priority: editPri, body: editBody, needed_by: editNeeded || null }))}
                    className="px-3 py-1.5 rounded-lg bg-bardin-ink text-white text-xs font-semibold disabled:opacity-50">Save</button>
          </div>
        </div>
      )}

      {err && <div className="mt-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-1.5 text-xs text-rose-800">{err}</div>}

      {/* Thread + attachments */}
      {expanded && (
        <div className="mt-3 border-t border-dashed border-slate-200 pt-3 space-y-3">
          {attachments && attachments.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {attachments.map((a) =>
                (a.mime_type ?? "").startsWith("image/") ? (
                  <button key={a.id} type="button" onClick={() => openDoc(a)} title={a.file_name}
                          className="w-16 h-16 rounded-lg border border-slate-200 overflow-hidden bg-slate-100">
                    {urls[a.id]
                      ? <img src={urls[a.id]} alt={a.file_name} className="w-full h-full object-cover" />
                      : <span className="text-xl leading-[4rem]">🖼</span>}
                  </button>
                ) : (
                  <button key={a.id} type="button" onClick={() => openDoc(a)}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 max-w-full">
                    📎 <span className="truncate max-w-[12rem]">{a.file_name}</span>
                  </button>
                ),
              )}
            </div>
          )}

          {events === null ? (
            <div className="text-xs text-slate-400">Loading thread…</div>
          ) : events.length === 0 ? (
            <div className="text-xs text-slate-400">No replies yet.</div>
          ) : (
            <ul className="space-y-1.5">
              {events.map((ev) => {
                const who = name(ev.author_id);
                if (ev.kind === "status") {
                  const to = String(ev.payload?.to ?? "");
                  const label = to === "in_progress" ? "started it" : to === "waiting" ? `marked it waiting on ${WAITING_ON_LABEL[(ev.payload?.waiting_on as OpsWaitingOn) ?? "other"]}`
                    : to === "done" ? "marked it done" : to === "withdrawn" ? "withdrew it" : to === "new" ? "reopened it" : `moved it to ${to}`;
                  return (
                    <li key={ev.id} className="text-xs text-slate-500">
                      <span className="font-medium text-slate-700">{who}</span> {label}{ev.body ? ` — “${ev.body}”` : ""}
                      <span className="text-slate-400"> · {fmtWhen(ev.created_at)}</span>
                    </li>
                  );
                }
                if (ev.kind === "reassign") {
                  return (
                    <li key={ev.id} className="text-xs text-slate-500">
                      <span className="font-medium text-slate-700">{who}</span> reassigned it to{" "}
                      <span className="font-medium text-slate-700">{name(String(ev.payload?.to ?? ""))}</span>
                      <span className="text-slate-400"> · {fmtWhen(ev.created_at)}</span>
                    </li>
                  );
                }
                return (
                  <li key={ev.id} className="text-sm rounded-lg bg-slate-50 border border-slate-200 px-3 py-1.5">
                    <div className="text-[11px] text-slate-500"><span className="font-semibold text-slate-700">{who}</span> · {fmtWhen(ev.created_at)}</div>
                    <div className="text-bardin-ink whitespace-pre-wrap">{ev.body}</div>
                  </li>
                );
              })}
            </ul>
          )}

          {actor.memberId && (
            <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (reply.trim()) void run(() => addComment(r.id, actor.memberId as string, reply), () => setReply("")); }}>
              <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply…"
                     className="flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
              <button type="submit" disabled={busy || !reply.trim()}
                      className="px-3 py-1.5 rounded-lg bg-bardin-teal text-white text-xs font-semibold disabled:opacity-50">Send</button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
